import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import RadioGroup from "../components/RadioGroup";
import FormSection from "../components/FormSection";
import FormSelect from "../components/FormSelect";
import FormInput from "../components/FormInput";
import backArrow from "../images/Layer 51.svg";

const PersonalContactForm = ({
  primaryColor = "#1F3047",
  secondaryColor = "#349AC8",
  textColor = "#181818",
  placeholderColor = "#717171",
  onBack = () => {},
  onSubmit = () => {},
  data,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: data?.personalContact?.firstName || "",
    lastName: data?.personalContact?.lastName || "",
    dob: data?.personalContact?.dob || "",
    phone: data?.personalContact?.phone || "",
    ssnLast4: data?.personalContact?.ssnLast4 || "",
    address: data?.personalContact?.address || "",
    city: data?.personalContact?.city || "",
    state: data?.personalContact?.state || "",
    zip: data?.personalContact?.zip || "",
    DLSTATE: data?.personalContact?.DLSTATE || "",
    DLNUMBER: data?.personalContact?.DLNUMBER || "",
    email: data?.personalContact?.email || "",
    emergencyContact: {
      name: data?.personalContact?.emergencyContact?.name || "",
      relationship: data?.personalContact?.emergencyContact?.relationship || "",
      phone: data?.personalContact?.emergencyContact?.phone || "",
      email: data?.personalContact?.emergencyContact?.email || "",
    },
    maritalStatus: data?.personalContact?.maritalStatus || "",
    hasChildren: data?.personalContact?.hasChildren ?? null,
    isEmployed: data?.personalContact?.isEmployed ?? null,
    hasVehicle: data?.personalContact?.hasVehicle ?? null,
    children: data?.personalContact?.children ?? [],
    vehicleMake: data?.personalContact?.vehicleMake || "",
    vehicleModel: data?.personalContact?.vehicleModel || "",
    licensePlate: data?.personalContact?.licensePlate || "",
    insurance: data?.personalContact?.insurance || "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmittable, setIsSubmittable] = useState(false);

  const addChild = () => {
    setFormData((prev) => ({
      ...prev,
      children: [
        ...prev.children,
        {
          name: "",
          age: "",
          relationship: "", // Optional: add more fields as needed
        },
      ],
    }));
  };

  const mandatoryFields = [
    "firstName",
    "lastName",
    "phone",
    "ssnLast4",
    "address",
    "city",
    "state",
    "zip",
    "email",
  ];

  const validationRules = {
    firstName: {
      required: true,
      message: "First name is required",
    },
    lastName: {
      required: true,
      message: "Last name is required",
    },
    dob: {
      required: true,
      message: "Date of birth is required",
    },
    phone: {
      required: true,
      pattern: /^\d{10}$/,
      message: "Phone must be 10 digits only",
    },
    ssnLast4: {
      required: true,
      pattern: /^\d{4}$/,
      message: "Last 4 digits of SSN required",
    },
    address: {
      required: true,
      message: "Address is required",
    },
    city: {
      required: true,
      message: "City is required",
    },
    state: {
      required: true,
      message: "State is required",
    },
    zip: {
      required: true,
      pattern: /^\d{5}(-\d{4})?$/,
      message: "Valid ZIP code required",
    },
    maritalStatus: {
      required: true,
      message: "Marital status is required",
    },
  };

  useEffect(() => {
    validateForm();
  }, [formData]);

  const validateField = (name, value) => {
    const rule = validationRules[name];
    if (!rule) return "";

    if (rule.required) {
      if (value === null || value === undefined || value === "") {
        return rule.message;
      }
    }

    if (rule.pattern && value && !rule.pattern.test(value)) {
      return rule.message;
    }

    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    let hasErrors = false;

    mandatoryFields.forEach((field) => {
      if (field.includes(".")) {
        // Handle nested fields
        const [parent, child] = field.split(".");
        const error = validateField(field, formData[parent][child]);
        if (error) {
          if (!newErrors[parent]) newErrors[parent] = {};
          newErrors[parent][child] = error;
          // hasErrors = true;
          console.log(error);
        }
      } else {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          // hasErrors = true;
          console.log(error);
        }
      }
    });

    setErrors(newErrors);
    setIsSubmittable(!hasErrors);
    return hasErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleEmergencyContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [name]: value,
      },
    }));

    if (errors.emergencyContact?.[name]) {
      const error = validateField(`emergencyContact.${name}`, value);
      setErrors((prev) => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [name]: error,
        },
      }));
    }
  };

  const handleRadioChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validateForm();

    if (Object.keys(errors).length === 0) {
      onSubmit(formData);
    } else {
      const newTouched = {};
      mandatoryFields.forEach((field) => {
        if (field.includes(".")) {
          const [parent, child] = field.split(".");
          newTouched[parent] = { ...newTouched[parent], [child]: true };
        } else {
          newTouched[field] = true;
        }
      });
      setTouched(newTouched);

      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.querySelector(`[name="${firstErrorField}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  const getInputProps = (name) => ({
    name,
    value: formData[name] || "",
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] ? errors[name] : "",
    color: secondaryColor,
    textColor,
    placeholderColor,
  });

  const getEmergencyContactProps = (name) => ({
    name,
    value: formData.emergencyContact[name] || "",
    onChange: handleEmergencyContactChange,
    onBlur: handleBlur,
    error: touched.emergencyContact?.[name]
      ? errors.emergencyContact?.[name]
      : "",
    color: secondaryColor,
    textColor,
    placeholderColor,
  });

  return (
    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "48px",
          padding: "20px",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Main Title */}
        <h1
          style={{
            textAlign: "center",
            color: primaryColor,
            fontSize: "32px",
            fontFamily: "Mauren, sans-serif",
            fontWeight: "100",
            margin: 0,
          }}
        >
          Personal & Contact Information
        </h1>

        {/* Form Sections */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "32px",
          }}
        >
          {/* Personal Details Section */}
          <FormSection
            title="Personal Details"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            {/* Name Row */}
            <div style={formRowStyle}>
              <FormInput
                label="First Name*"
                {...getInputProps("firstName")}
                placeholder="Enter First Name"
              />
              <FormInput
                label="Last Name*"
                {...getInputProps("lastName")}
                placeholder="Enter Last Name"
              />
            </div>

            {/* DOB & Phone Row */}
            <div style={formRowStyle}>
              <FormInput
                label="Date of Birth"
                {...getInputProps("dob")}
                placeholder="MM/DD/YYYY"
              />
              <PhoneNumberInput
                label="Phone Number*"
                {...getInputProps("phone")}
                onChange={(e) => {
                  // This will receive the raw digits only
                  getInputProps("phone").onChange(e);
                }}
              />
            </div>

            {/* SSN & Address Row */}
            <div style={formRowStyle}>
              <FormInput
                label="Last 4 of SSN*"
                {...getInputProps("ssnLast4")}
                placeholder="XXX-XX-____"
              />
              <FormInput
                label="Street Address*"
                {...getInputProps("address")}
                placeholder="Ex: 123 Main St Apt 4B"
              />
            </div>

            <div style={formRowStyle}>
              <FormInput
                label="Email*"
                {...getInputProps("email")}
                placeholder="Eg: JohnDoe@email.com"
              />
              <FormInput
                label="DL STATE"
                {...getInputProps("DLSTATE")}
                placeholder="XXX-XX"
              />
              <FormInput
                label="DL NUMBER"
                {...getInputProps("DLNUMBER")}
                placeholder="XXX-XX"
              />
            </div>

            {/* City, State, Zip Row */}
            <div style={formRowStyle}>
              <FormInput
                label="City*"
                {...getInputProps("city")}
                placeholder="Required"
              />
              <FormSelect
                label="State*"
                {...getInputProps("state")}
                placeholder="List of U.S. states"
                options={[
                  "Alabama",
                  "Alaska",
                  "Arizona",
                  "Arkansas",
                  "California",
                  "Colorado",
                  "Connecticut",
                  "Delaware",
                  "Florida",
                  "Georgia",
                  "Hawaii",
                  "Idaho",
                  "Illinois",
                  "Indiana",
                  "Iowa",
                  "Kansas",
                  "Kentucky",
                  "Louisiana",
                  "Maine",
                  "Maryland",
                  "Massachusetts",
                  "Michigan",
                  "Minnesota",
                  "Mississippi",
                  "Missouri",
                  "Montana",
                  "Nebraska",
                  "Nevada",
                  "New Hampshire",
                  "New Jersey",
                  "New Mexico",
                  "New York",
                  "North Carolina",
                  "North Dakota",
                  "Ohio",
                  "Oklahoma",
                  "Oregon",
                  "Pennsylvania",
                  "Rhode Island",
                  "South Carolina",
                  "South Dakota",
                  "Tennessee",
                  "Texas",
                  "Utah",
                  "Vermont",
                  "Virginia",
                  "Washington",
                  "West Virginia",
                  "Wisconsin",
                  "Wyoming",
                  "District of Columbia",
                ]}
              />
              <FormInput
                label="ZIP Code*"
                {...getInputProps("zip")}
                placeholder=""
              />
            </div>
          </FormSection>

          {/* Emergency Contact Section */}
          <FormSection
            title="Emergency Contact Information"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <FormInput
                label="Full Name"
                {...getEmergencyContactProps("name")}
                placeholder="Enter Full Name"
              />
              <FormSelect
                label="Relationship"
                {...getEmergencyContactProps("relationship")}
                placeholder="How do you know the client?"
                options={[
                  "Parent",
                  "Spouse",
                  "Sibling",
                  "Child",
                  "Grandparent",
                  "Friend",
                  "Partner",
                  "Roommate",
                  "Coworker",
                  "Neighbor",
                  "Legal Guardian",
                  "Other",
                ]}
              />
            </div>
            <div style={formRowStyle}>
              <PhoneNumberInput
                label="Phone Number*"
                {...getEmergencyContactProps("phone")}
                onChange={(e) => {
                  // This will receive the raw digits only
                  getEmergencyContactProps("phone").onChange(e);
                }}
              />
              {/* <FormInput
                label="Phone Number"
                {...getEmergencyContactProps("phone")}
                placeholder="(XXX) XXX-XXXX"
              /> */}
              <FormInput
                label="Email (Optional)"
                {...getEmergencyContactProps("email")}
                placeholder="Eg: JohnDoe@email.com"
              />
            </div>
          </FormSection>

          {/* Personal Status Section */}
          <FormSection
            title="Personal Status"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <FormSelect
                label="Marital Status"
                {...getInputProps("maritalStatus")}
                placeholder="What is your current marital status?"
                options={[
                  "Single",
                  "Married",
                  "Divorced",
                  "Widowed",
                  "Separated",
                  "Domestic Partnership",
                  "Prefer not to say",
                ]}
              />
            </div>
            <div style={formRowStyle}>
              <RadioGroup
                label="Do you have children?"
                name="hasChildren"
                value={formData.hasChildren}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("hasChildren", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
                error={touched.hasChildren ? errors.hasChildren : ""}
              />
            </div>

            {formData.hasChildren && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {/* Render existing children */}
                {formData.children.map((child, index) => (
                  <div key={index} style={formRowStyle}>
                    <FormInput
                      label="Child's Name"
                      value={child.name}
                      onChange={(e) => {
                        const updatedChildren = [...formData.children];
                        updatedChildren[index].name = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          children: updatedChildren,
                        }));
                      }}
                      placeholder="Full name"
                    />
                    <FormInput
                      label="Age"
                      value={child.age}
                      onChange={(e) => {
                        const updatedChildren = [...formData.children];
                        updatedChildren[index].age = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          children: updatedChildren,
                        }));
                      }}
                      placeholder="Age"
                    />
                    {/* Add remove button for each child */}
                    <button
                      type="button"
                      onClick={() => {
                        const updatedChildren = formData.children.filter(
                          (_, i) => i !== index
                        );
                        setFormData((prev) => ({
                          ...prev,
                          children: updatedChildren,
                        }));
                      }}
                      style={{
                        alignSelf: "flex-end",
                        background: "none",
                        border: "none",
                        color: secondaryColor,
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                {/* Update the button to add children */}
                <button
                  type="button"
                  onClick={addChild}
                  style={{
                    padding: "12px 16px",
                    background: secondaryColor,
                    borderRadius: "16px",
                    border: "none",
                    color: "white",
                    fontSize: "16px",
                    fontFamily:
                      "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                    fontWeight: "400",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    alignSelf: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "2px",
                        background: "white",
                        position: "absolute",
                      }}
                    />
                    <div
                      style={{
                        width: "2px",
                        height: "12px",
                        background: "white",
                        position: "absolute",
                      }}
                    />
                  </div>
                  Add Another Child
                </button>
              </div>
            )}
          </FormSection>

          {/* Employment Section */}
          <FormSection
            title="Employment Information"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <RadioGroup
                label="Are you currently employed?"
                name="isEmployed"
                value={formData.isEmployed}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("isEmployed", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
                error={touched.isEmployed ? errors.isEmployed : ""}
              />
            </div>

            {formData.isEmployed && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div style={formRowStyle}>
                  <FormInput
                    label="Current Salary"
                    {...getInputProps("salary")}
                    placeholder="Annual salary"
                  />
                  <FormSelect
                    label="Employment Type*"
                    {...getInputProps("employmentType")}
                    placeholder="Select employment type"
                    options={["Full-time", "Part-time"]}
                  />
                </div>

                <div style={formRowStyle}>
                  <FormInput
                    label="Employer Name"
                    {...getInputProps("employerName")}
                    placeholder="Company name"
                  />
                  <FormInput
                    label="Contact Number"
                    {...getInputProps("employerContact")}
                    placeholder="Phone number"
                  />
                </div>
              </div>
            )}
          </FormSection>
          <FormSection
            title="Vehicle Information"
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          >
            <div style={formRowStyle}>
              <RadioGroup
                label="Do you have a vehicle?"
                name="hasVehicle"
                value={formData.hasVehicle}
                options={["Yes", "No"]}
                onChange={(value) =>
                  handleRadioChange("hasVehicle", value === "Yes")
                }
                color={primaryColor}
                textColor={textColor}
                error={touched.hasVehicle ? errors.hasVehicle : ""}
              />
            </div>

            {formData.hasVehicle && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 16,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>
                      MAKE
                    </th>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>
                      MODEL
                    </th>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>
                      TAG #
                    </th>
                    <th style={{ textAlign: "left", paddingBottom: 8 }}>
                      INSURANCE
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <FormInput
                        {...getInputProps("vehicleMake")}
                        placeholder="Ex: Toyota Camry"
                      />
                    </td>
                    <td>
                      <FormInput
                        {...getInputProps("vehicleModel")}
                        placeholder="Ex: Toyota Camry"
                      />
                    </td>
                    <td>
                      <FormInput
                        {...getInputProps("licensePlate")}
                        placeholder="License number"
                      />
                    </td>
                    <td>
                      <FormInput
                        {...getInputProps("insurance")}
                        placeholder="Insurance provider"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </FormSection>
        </div>

        {/* Form Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "24px",
            width: "100%",
            marginTop: "20px",
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "12px 16px",
              borderRadius: "16px",
              outline: `1px solid ${secondaryColor}`,
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                padding: "6px",
                background: secondaryColor,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src={backArrow} alt="" />
            </div>

            <span
              style={{
                color: primaryColor,
                fontSize: "16px",
                fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
                fontWeight: "400",
              }}
            >
              Back to Welcome
            </span>
          </button>

          <button
            type="submit"
            style={{
              padding: "12px 16px",
              background: isSubmittable ? secondaryColor : "#cccccc",
              borderRadius: "16px",
              border: "none",
              color: "white",
              fontSize: "16px",
              fontFamily: "'FONTSPRING DEMO - Commuters Sans', sans-serif",
              fontWeight: "400",
              cursor: isSubmittable ? "pointer" : "not-allowed",
              transition: "all 0.3s ease",
              ":hover": {
                opacity: 0.9,
              },
            }}
            disabled={!isSubmittable}
          >
            Continue to Residency Details
          </button>
        </div>
      </div>
    </form>
  );
};

const formRowStyle = {
  display: "flex",
  gap: "24px",
  width: "100%",
};

PersonalContactForm.propTypes = {
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  placeholderColor: PropTypes.string,
  onBack: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default PersonalContactForm;

const PhoneNumberInput = ({ value, onChange, ...props }) => {
  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    // Format the initial value if it exists
    if (value) {
      setDisplayValue(formatPhoneNumber(value));
    }
  }, [value]);

  const formatPhoneNumber = (input) => {
    // Remove all non-digit characters
    const cleaned = input.replace(/\D/g, "");

    // Apply formatting
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return "";

    let formatted = "";
    if (match[1]) formatted += `(${match[1]}`;
    if (match[2]) formatted += `)-${match[2]}`;
    if (match[3]) formatted += `-${match[3]}`;

    return formatted;
  };

  const handleChange = (e) => {
    const input = e.target.value;
    const formatted = formatPhoneNumber(input);
    setDisplayValue(formatted);

    // Send the unformatted value to the parent (just digits)
    const digitsOnly = formatted.replace(/\D/g, "");
    onChange({
      ...e,
      target: {
        ...e.target,
        value: digitsOnly,
        name: e.target.name,
      },
    });
  };

  return (
    <FormInput
      {...props}
      value={displayValue}
      onChange={handleChange}
      placeholder="(XXX)-XXX-XXXX"
    />
  );
};
